// [UPDATED] Requirement 5: goals, KRAs and KPIs are created by the employee for themselves. The manager only
// views them here and approves / returns them (Goal approvals, KRA & KPI approvals). All "Assign goal", edit and
// remove-assigned-goal code for managers is removed from this page.
import { useNavigate, useParams } from "react-router-dom";
import { FiCheckSquare, FiClipboard, FiMail, FiMapPin, FiCalendar } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { getEmployeeGoalSheet } from "../../services/goalApi";
import { getEmployeeKraKpi } from "../../services/kraKpiApi";
import { getCycleRatingConfig } from "../../services/settingsApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Loader, ErrorState, Button, StatusBadge, Avatar, Banner } from "../../components/common";
import WeightageMeter from "../../components/goals/WeightageMeter";
import GoalList from "../../components/goals/GoalList";
import GoalQuota from "../../components/goals/GoalQuota";
import { KraTable, KpiTable } from "../../components/performance/KraKpiTable";
import ScoreBreakdown from "../../components/performance/ScoreBreakdown";
import KpiMeasurements from "../../components/performance/KpiMeasurements";
import { computePerformance } from "../../utils/scoreCalculator";
import { formatDate } from "../../utils/format";
import { APPROVED_GOAL_STATUSES, SHEET_STATUS, MAX_GOALS_PER_YEAR } from "../../utils/constants";
import { listProgressApprovals } from "../../services/progressApprovalApi";
import { Link } from "react-router-dom";

const api = { getEmployeeGoalSheet, getEmployeeKraKpi, getCycleRatingConfig };

export default function TeamMemberPage() {
  const { employeeId } = useParams();
  const { cycleId } = useCycle();
  const navigate = useNavigate();
  const { data, error, reload } = useAsync(async () => {
    const [sheet, assignments, rating] = await Promise.all([
      api.getEmployeeGoalSheet(cycleId, employeeId),
      api.getEmployeeKraKpi(cycleId, employeeId),
      api.getCycleRatingConfig(cycleId),
    ]);
    return { sheet: sheet.data, assignments: assignments.data, rating: rating.data };
  }, [cycleId, employeeId], { skip: !cycleId });

  const pendingProgress = useAsync(async () => (await listProgressApprovals({ view: "review", status: "PENDING" })).data.filter((r) => r.employeeId === employeeId), [employeeId, cycleId]); // [NEW]
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const { sheet, assignments, rating } = data;
  const emp = sheet.employee;
  const perf = computePerformance({
    goals: sheet.goals.filter((g) => APPROVED_GOAL_STATUSES.includes(g.status)),
    kras: assignments.kras,
    kpis: assignments.kpis,
    config: rating,
  });

  const AFTER_GOALS = ["TRACKING", "SELF_ASSESSMENT", "MANAGER_ASSESSMENT", "HR_FINALIZATION", "CLOSED"];

  let assessmentNote = null;
  if (AFTER_GOALS.includes(sheet.currentStage) && !sheet.resultFinalized) {
    if (sheet.selfStatus !== "Submitted") {
      assessmentNote = {
        tone: "info",
        title: `Self assessment: ${sheet.selfStatus}`,
        text: `Goal progress and KPI measurements below are updates during the year. Your manager assessment opens after ${emp.name} submits the self assessment (Self assessment → Submit to manager). Marking a goal completed does not submit it.`,
      };
    } else if (sheet.managerStatus !== "Submitted") {
      assessmentNote = {
        tone: "success",
        title: "Self assessment submitted — your review is due",
        text: `Click "Open assessment" to enter the agreed achievement, comments and submit to HR.`,
      };
    }
  }

  const usage = sheet.goalUsage;
  return (
    <div className="page">
      <PageHeader
        backTo="/team"
        backLabel="Team members"
        title={emp.name}
        subtitle={`${emp.designationName}, ${emp.departmentName}`}
        actions={
          <>
            {sheet.sheetStatus === SHEET_STATUS.PENDING && (
              <Button icon={FiCheckSquare} onClick={() => navigate(`/team/goal-approvals/${employeeId}`)}>Review goals</Button>
            )}
            <Button variant="secondary" icon={FiClipboard} onClick={() => navigate(`/team/assessments/${employeeId}`)}>Open assessment</Button>
          </>
        }
      />

      {(pendingProgress.data || []).length > 0 && (
        <Banner tone="warning" title={`${pendingProgress.data.length} progress update${pendingProgress.data.length === 1 ? "" : "s"} from ${emp.name} waiting for you`}>
          {pendingProgress.data.map((x) => `${x.itemTitle} (${x.itemType}, ${x.progress ?? "—"}%)`).join(", ")}.{" "}
          <Link to="/team/progress-approvals">Approve or return with changes</Link>
        </Banner>
      )}
      {assessmentNote && (
        <Banner
          tone={assessmentNote.tone}
          title={assessmentNote.title}
          action={sheet.selfStatus === "Submitted" && <Button size="sm" icon={FiClipboard} onClick={() => navigate(`/team/assessments/${employeeId}`)}>Open assessment</Button>}
        >
          {assessmentNote.text}
        </Banner>
      )}
      <Banner tone="info" title={`${emp.name} sets their own goals, KRAs and KPIs`}>
        Employees add their own goals, KRAs and KPIs. You review them under Goal approvals and KRA &amp; KPI approvals
        (approve, edit with a comment, or return for changes). Managers do not assign them.
      </Banner>

      <div className="grid-1-2">
        <Card>
          <div className="profile-mini">
            <Avatar name={emp.name} size={56} />
            <div>
              <p className="profile-mini-name">{emp.name}</p>
              <p className="muted">{emp.id}</p>
            </div>
          </div>
          <ul className="icon-list">
            <li><FiMail size={14} aria-hidden="true" /> {emp.email}</li>
            <li><FiMapPin size={14} aria-hidden="true" /> {emp.location}</li>
            <li><FiCalendar size={14} aria-hidden="true" /> Joined {formatDate(emp.dateOfJoining)}</li>
          </ul>
          <dl className="kv-list">
            <div><dt>Goal sheet</dt><dd><StatusBadge status={sheet.sheetStatus} /></dd></div>
          </dl>
        </Card>
        <Card>
          <ScoreBreakdown performance={perf} weights={rating.weights} restricted={rating.restricted} scoreCap={rating.scoreCap} title="Current estimate" provisional />
        </Card>
      </div>

      <Card title={`Goals this year (max ${MAX_GOALS_PER_YEAR})`} subtitle={`${emp.name} can set at most ${MAX_GOALS_PER_YEAR} goals per year.`}>
        <GoalQuota usage={usage} who={emp.name} />
      </Card>
      <Card title="Goal percentage">
        <WeightageMeter goals={sheet.goals} compact />
      </Card>
      <Card padded={false}>
        <GoalList
          goals={sheet.goals}
          emptyTitle="No goals yet"
          emptyText={`${emp.name} has not added goals yet.`}
        />
      </Card>
      <Card title="KRAs (daily tasks)" padded={false}><KraTable rows={assignments.kras} /></Card>
      <Card title="KPIs" padded={false}><KpiTable rows={assignments.kpis} /></Card>
      {assignments.kpis.length > 0 && (
        <>
          <h2 className="section-title">KPI measurements recorded by {emp.name}</h2>
          <p className="muted" style={{ margin: "-4px 0 10px" }}>Read-only. These are the values {emp.name} recorded during the year under My KRA &amp; KPI. The Actual used for the score is entered in the self assessment and agreed in your manager assessment.</p>
          {assignments.kpis.map((kpi) => (
            <KpiMeasurements key={kpi.id} kpi={kpi} readOnlyNote={`${emp.name} has not recorded a value for this KPI yet.`} />
          ))}
        </>
      )}

    </div>
  );
}
